import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Calendar, Clock, Users, MapPin, LogOut, XCircle, AlertTriangle, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

function isReservationPast(r) {
  try {
    const t = r.time?.trim().toUpperCase() || "";
    let h = 0, m = 0;
    if (t.includes("AM") || t.includes("PM")) {
      const parts = t.replace(/[AP]M/g, "").trim().split(":");
      h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0;
      if (t.includes("PM") && h !== 12) h += 12;
      else if (t.includes("AM") && h === 12) h = 0;
    } else { const parts = t.split(":"); h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0; }
    const [y, mo, d] = (r.date || "").split("-").map(Number);
    return new Date(y, mo - 1, d, h, m) < new Date();
  } catch { return false; }
}

function canClientCancel(r, cancelHours) {
  try {
    const t = r.time?.trim().toUpperCase() || "";
    let h = 0, m = 0;
    if (t.includes("AM") || t.includes("PM")) {
      const parts = t.replace(/[AP]M/g, "").trim().split(":");
      h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0;
      if (t.includes("PM") && h !== 12) h += 12;
      else if (t.includes("AM") && h === 12) h = 0;
    } else { const parts = t.split(":"); h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0; }
    const [y, mo, d] = (r.date || "").split("-").map(Number);
    const resDate = new Date(y, mo - 1, d, h, m);
    const deadline = new Date(resDate.getTime() - cancelHours * 60 * 60 * 1000);
    return new Date() < deadline;
  } catch { return false; }
}

export default function MyReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelHours, setCancelHours] = useState(2);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    loadReservations();
    API.get("/landing").then(r => setCancelHours(r.data.cancellation_hours || 2)).catch(() => {});
  }, []);

  const loadReservations = async () => {
    try {
      const { data } = await API.get("/reservations/my");
      setReservations(data);
    } catch {
      toast.error("Error al cargar reservas");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await API.post(`/reservations/${cancelDialog}/cancel`, { reason: cancelReason, cancelled_by: "client" });
      toast.success("Reserva cancelada");
      setCancelDialog(null);
      setCancelReason("");
      loadReservations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cancelar");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const displayName = user?.name || "";

  const { theme, setTheme } = useTheme();
  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8" data-testid="my-reservations-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Button variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-muted" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Inicio
          </Button>
          <div className="flex items-center gap-2">
            <Button data-testid="client-theme-toggle" variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground h-8 w-8 p-0" onClick={toggleTheme} title={theme === "dark" ? "Modo claro" : "Modo oscuro"}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button data-testid="logout-btn" variant="ghost" className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>

        <h1 className="text-3xl font-bold mb-2" data-testid="my-reservations-title">Mis Reservas</h1>
        <p className="text-muted-foreground mb-8" data-testid="greeting">Hola, {displayName || user?.email}</p>

        {loading ? (
          <div className="text-center text-muted-foreground py-12">Cargando...</div>
        ) : reservations.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tienes reservas aun</p>
              <Button className="mt-4 bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b]" onClick={() => navigate("/reservar")}>
                Hacer una reserva
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reservations.map(r => {
              const past = isReservationPast(r);
              const canCancel = !past && r.status !== "cancelled" && canClientCancel(r, cancelHours);
              return (
                <Card key={r.id} className={`bg-card border-border ${past ? 'opacity-60' : ''}`} data-testid={`reservation-card-${r.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-[#c9a84c] font-bold text-lg tracking-wider">{r.reservation_code}</span>
                        <Badge
                          variant="outline"
                          className={`ml-3 font-medium ${r.status === "confirmed" ? "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700" : r.status === "created" ? "bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-700" : "bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-700"}`}
                        >
                          {r.status === "confirmed" ? "Confirmada" : r.status === "created" ? "Creada" : "Cancelada"}
                        </Badge>
                      </div>
                      {canCancel ? (
                        <Button data-testid={`cancel-reservation-${r.id}`} variant="ghost" size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          onClick={() => setCancelDialog(r.id)}>
                          <XCircle className="w-4 h-4 mr-1" /> Cancelar
                        </Button>
                      ) : r.status !== "cancelled" && !past ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground" title={`Solo puedes cancelar hasta ${cancelHours}h antes`}>
                          <AlertTriangle className="w-3 h-3" /> Fuera del plazo
                        </span>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-4 h-4 text-[#c9a84c]" /><span>{r.sede_name}</span></div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4 text-[#c9a84c]" /><span>{r.date}</span></div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Clock className="w-4 h-4 text-[#c9a84c]" /><span>{r.time}</span></div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Users className="w-4 h-4 text-[#c9a84c]" /><span>{r.num_people} personas</span></div>
                    </div>
                    {r.cancellation_reason && (
                      <p className="mt-3 text-sm text-red-400 bg-red-900/10 rounded p-2">Motivo: {r.cancellation_reason}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!cancelDialog} onOpenChange={() => { setCancelDialog(null); setCancelReason(""); }}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2"><XCircle className="w-5 h-5" /> Cancelar Reserva</DialogTitle>
            <DialogDescription>Tu reserva sera cancelada.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="cancel-reason-input"
            placeholder="Motivo de cancelaci&oacute;n (opcional)"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            className="bg-background border-border"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelDialog(null); setCancelReason(""); }}>Volver</Button>
            <Button data-testid="confirm-cancel-btn" variant="destructive" onClick={handleCancel}>Cancelar Reserva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
